import { useLiveQuery } from "dexie-react-hooks";
import { detentionPay, formatUsd, newId, type Detention } from "@haulsheet/core";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { useNow } from "../hooks/useNow";
import { TextInput } from "../components/ui/Field";
import { getDb } from "../db/dexie";
import {
  departedAtFor,
  detentionDefaults,
  formatElapsed,
  formatHours,
  parseMoneyCents,
} from "../logs/detention";
import { useAuthStore } from "../store/authStore";
import { useUnsavedGuard } from "../pwa/unsaved";
import { getSyncClient } from "../sync/SyncClient";


/**
 * Detention clock. The running timer is just a detention row with
 * departedAt = null in Dexie, so it survives reloads, app kills and dead
 * zones, and syncs to the other devices like any other row.
 */
export function LogsDetentionScreen() {
  const me = useAuthStore((s) => s.me);
  const rows = useLiveQuery(async () => {
    const list = await getDb().detention.toArray();
    return list
      .filter((d) => d.deletedAt == null)
      .sort((a, b) => b.arrivedAt.localeCompare(a.arrivedAt));
  }, []);
  // Several clocks can run (a stop's clock and an older one not tied to a
  // load). ?id= picks one (Loads links each to its own); the others are listed.
  const [params] = useSearchParams();
  const runningAll = (rows ?? []).filter((r) => r.departedAt == null);
  const running = runningAll.find((r) => r.id === params.get("id")) ?? runningAll[0] ?? null;
  const others = runningAll.filter((r) => r.id !== running?.id);
  const done = (rows ?? []).filter((r) => r.departedAt != null);
  const now = useNow(running != null);

  const defaults = detentionDefaults(rows ?? []);
  const [place, setPlace] = useState("");
  const [free, setFree] = useState<string | null>(null);
  const [rate, setRate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  useUnsavedGuard("logs-detention", place.trim() !== "" || free != null || rate != null);
  const freeText = free ?? String(defaults.freeHours);
  const rateText = rate ?? (defaults.rateCentsPerHour / 100).toFixed(2);

  async function start() {
    if (!me) return;
    setError(null);
    if (running) return;
    const freeHours = Number(freeText.trim());
    if (freeText.trim() === "" || !Number.isFinite(freeHours) || freeHours < 0 || freeHours > 24) {
      setError("Free hours must be 0–24");
      return;
    }
    const rateCents = parseMoneyCents(rateText);
    if (rateCents == null || rateCents > 100000) {
      setError("Enter a rate per hour like 50");
      return;
    }
    const id = newId();
    const at = new Date().toISOString();
    // Started by hand: tie it to the load you're on when there's exactly one
    // rolling load of yours (and to the stop you're at); otherwise leave it
    // unlinked — arriving at a stop on a load adopts it later.
    const link = await currentLoadLink(me.role === "driver" ? me.driverId : null);
    const row: Detention = {
      id,
      orgId: me.org.id,
      version: 0,
      createdAt: at,
      updatedAt: at,
      deletedAt: null,
      loadId: link?.loadId ?? null,
      loadStopId: link?.stopId ?? null,
      placeText: place.trim() || link?.placeText || null,
      arrivedAt: at,
      departedAt: null,
      freeHours,
      rateCentsPerHour: rateCents,
      payCents: null,
    } as Detention;
    await getSyncClient().mutate({
      entity: "detention",
      op: "upsert",
      id,
      baseVersion: null,
      row: row as unknown as Record<string, unknown>,
    });
    setPlace("");
    setFree(null);
    setRate(null);
  }

  async function stop() {
    if (!running) return;
    // Re-read: another device may have stopped it, or the row synced.
    const row = await getDb().detention.get(running.id);
    if (!row || row.departedAt != null || row.deletedAt != null) return;
    const departedAt = departedAtFor(row.arrivedAt);
    const { payCents } = detentionPay({
      arrivedAt: row.arrivedAt,
      departedAt,
      freeHours: row.freeHours,
      rateCentsPerHour: row.rateCentsPerHour,
    });
    const patch = { departedAt, payCents };
    await getSyncClient().mutate({
      entity: "detention",
      op: "upsert",
      id: row.id,
      baseVersion: row.version || null,
      patch,
      row: { ...row, ...patch, updatedAt: new Date().toISOString() },
    });
    setConfirmDiscard(false);
  }

  async function discard() {
    if (!running) return;
    if (!confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    await getSyncClient().mutate({
      entity: "detention",
      op: "delete",
      id: running.id,
      baseVersion: running.version || null,
    });
    setConfirmDiscard(false);
  }

  const live = running
    ? detentionPay({
        arrivedAt: running.arrivedAt,
        departedAt: Math.max(now, new Date(running.arrivedAt).getTime()),
        freeHours: running.freeHours,
        rateCentsPerHour: running.rateCentsPerHour,
      })
    : null;
  const freeLeftMs = running && live ? Math.max(0, (running.freeHours - live.hours) * 3_600_000) : 0;

  return (
    <div className="stack">
      <h1 className="screen-title">Detention</h1>
      <div className="row">
        <Link to="/logs/fuel" className="chip">
          Fuel
        </Link>
        <Link to="/logs/maintenance" className="chip">
          Maintenance
        </Link>
      </div>

      {running && live ? (
        <section className="card stack" aria-label="Detention running" data-testid="detention-running">
          <div className="muted">On the clock{running.placeText ? ` at ${running.placeText}` : ""}</div>
          <div style={{ fontSize: "2.25rem", fontVariantNumeric: "tabular-nums" }} data-testid="detention-elapsed">
            {formatElapsed(live.hours * 3_600_000)}
          </div>
          <div className="muted">
            Since {new Date(running.arrivedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} ·{" "}
            {running.freeHours}h free · {formatUsd(running.rateCentsPerHour)}/hr
          </div>
          <div data-testid="detention-live-pay">
            {freeLeftMs > 0
              ? `Free time left ${formatElapsed(freeLeftMs)}`
              : `Billable ${formatHours(live.billableHours)} · ${formatUsd(live.payCents)}`}
          </div>
          <div className="row">
            <Button variant="primary" onClick={() => void stop()}>
              Stop detention
            </Button>
            <Button variant="ghost" onClick={() => void discard()}>
              {confirmDiscard ? "Tap again to discard" : "Discard"}
            </Button>
          </div>
          {others.length > 0 ? (
            <div className="stack" style={{ gap: "0.25rem" }} data-testid="detention-others">
              <div className="muted">Also running:</div>
              {others.map((o) => (
                <Link key={o.id} to={`/logs/detention?id=${o.id}`} className="tap-link" data-testid="detention-other">
                  {o.placeText ?? "Not tied to a load"} · {formatElapsed(now - new Date(o.arrivedAt).getTime())}
                </Link>
              ))}
            </div>
          ) : null}
        </section>
      ) : (
        <form
          className="card stack"
          aria-label="Start detention"
          onSubmit={(e) => {
            e.preventDefault();
            void start();
          }}
        >
          <TextInput
            label="Where (optional)"
            placeholder="Receiver, city"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
          />
          <div className="row">
            <TextInput
              label="Free hours"
              inputMode="decimal"
              value={freeText}
              onChange={(e) => setFree(e.target.value)}
            />
            <TextInput
              label="Rate $/hr"
              inputMode="decimal"
              value={rateText}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
          {error ? (
            <p className="legal-flag" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          ) : null}
          <Button variant="primary" type="submit">
            Start detention
          </Button>
        </form>
      )}

      <div className="card" style={{ padding: 0 }} data-testid="detention-list">
        {running && live ? (
          <div className="list-item" data-testid="detention-row-running" aria-label="Detention running now">
            <div>
              <div>
                {running.placeText ?? "Stop"} <span className="chip">Running</span>
              </div>
              <div className="muted">
                {new Date(running.arrivedAt).toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}{" "}
                · <span data-testid="detention-row-elapsed">{formatElapsed(live.hours * 3_600_000)}</span> so far
              </div>
            </div>
            <div>{freeLeftMs > 0 ? "Free time" : formatUsd(live.payCents)}</div>
          </div>
        ) : null}
        {done.length === 0 && !running ? (
          <p className="muted" style={{ padding: "1rem" }}>
            No detention yet
          </p>
        ) : (
          done.map((r) => {
            const hours = (new Date(r.departedAt!).getTime() - new Date(r.arrivedAt).getTime()) / 3_600_000;
            return (
              <div key={r.id} className="list-item" data-testid="detention-row">
                <div>
                  <div>{r.placeText ?? "Stop"}</div>
                  <div className="muted">
                    {new Date(r.arrivedAt).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    · {formatHours(hours)}
                  </div>
                </div>
                <div>{r.payCents != null ? formatUsd(r.payCents) : "—"}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/** The single rolling load (yours, if you're a driver) and the stop you're at on it, if any. */
async function currentLoadLink(driverId: string | null): Promise<{ loadId: string; stopId: string | null; placeText: string | null } | null> {
  const db = getDb();
  const rolling = (await db.load.toArray()).filter(
    (l) => l.deletedAt == null && l.status === "rolling" && (driverId == null || l.driverId === driverId),
  );
  if (rolling.length !== 1) return null;
  const load = rolling[0]!;
  const at = (await db.load_stop.where("loadId").equals(load.id).toArray())
    .filter((s) => s.deletedAt == null && s.arrivedAt != null && s.departedAt == null)
    .sort((a, b) => a.seq - b.seq)[0];
  return { loadId: load.id, stopId: at?.id ?? null, placeText: at?.place.text || null };
}
